'use client';

import { useEffect, useRef } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { useLatest } from 'react-use';

import { useConfigValue, useTenantName } from '@/components/providers/config-provider';
import { useSafePostHog } from '@/hooks/use-safe-posthog';
import {
  buildThreadStreamUrl,
  hasResumableRecordForThread,
  primeResumableRecord,
} from '@/lib/hitl/resumable-storage';
import { resolveServerResourceId } from '@/lib/utils/resource-id';
import { findThread } from '@/lib/utils/thread';

/**
 * Recovers streams whose id the client never received: reloading during a send loses the
 * `x-resumable-stream-id` response header (it only exists on the original response), leaving a
 * live server-side stream the resume check can't see. When the active thread has no local
 * record, ask the backend whether that thread has a live stream and prime the record — the
 * resume poll picks it up within its window.
 */
export function useResumableStreamDiscovery(): void {
  const resumableEnabled = useConfigValue<boolean>('resumableStreaming') === true;
  const tenantName = useTenantName();
  const posthog = useSafePostHog();
  const mainThreadId = useAuiState(state => state.threads.mainThreadId);
  const threadItems = useAuiState(state => state.threads.threadItems);
  const checkedThreadsRef = useRef<Set<string>>(new Set());
  const lastMessageRole = useAuiState(state => state.thread.messages.at(-1)?.role);
  const isRunning = useAuiState(state => state.thread.isRunning);
  const threadCompleteRef = useLatest(lastMessageRole === 'assistant' && !isRunning);

  useEffect(() => {
    if (!resumableEnabled || !mainThreadId) return;
    const threadUuid = findThread(threadItems, mainThreadId)?.remoteId;
    if (!threadUuid || checkedThreadsRef.current.has(threadUuid)) return;
    if (hasResumableRecordForThread({ tenantName, threadUuid })) return;
    checkedThreadsRef.current.add(threadUuid);

    const resourceId = resolveServerResourceId(posthog?.get_distinct_id());
    const url = buildThreadStreamUrl({ tenantName, threadUuid, resourceId });

    // Retried: right after a reload-during-send the backend may not have written the binding
    // yet (it lands when the chat response is created, ~1-2s into the request).
    const attemptDelaysMs = [0, 750, 1500, 3000, 5000];
    const attempt = async (attemptIndex: number): Promise<void> => {
      // The rehydrated thread already ends with a completed reply: the run finished while we
      // were away, so replaying it would only re-animate a finished conversation (trail flash).
      if (threadCompleteRef.current) {
        return;
      }
      try {
        const response = await fetch(url, { credentials: 'include' });
        if (response.status === 200) {
          const { streamId } = (await response.json()) as { streamId?: string };
          if (streamId) primeResumableRecord({ tenantName, streamId, threadUuid });
          return;
        }
      } catch {
        // Discovery is best-effort; rehydration already covers finished runs.
      }
      const nextDelay = attemptDelaysMs[attemptIndex + 1];
      if (nextDelay !== undefined) setTimeout(() => void attempt(attemptIndex + 1), nextDelay);
    };
    void attempt(0);
  }, [resumableEnabled, tenantName, mainThreadId, threadItems, posthog, threadCompleteRef]);
}
